/**
 * Merge a server-authoritative message list with a local in-memory list,
 * deduplicating by id, preferring the server copy, preserving any
 * local-only entries, and ordering by createdAt/timestamp ASC.
 *
 * Why this exists (2026-05-15): UnifiedChatPanel keeps `allMessages` in
 * React state alongside Apollo's cache for GET_MESSAGES_PAGINATED. When
 * a sendMessage mutation rewrites the cache, useQuery re-emits and the
 * sync effect previously *replaced* `allMessages` wholesale, wiping any
 * subscription-delivered message that hadn't yet been written into the
 * cache. The companion fix in the ON_NEW_MESSAGE handler writes
 * subscription messages into the cache so the divergence stops happening;
 * this merge is the seatbelt for any future ingress path that forgets to
 * keep the cache in sync.
 *
 * @param {Array&lt;{id: string, createdAt?: string, timestamp?: string}&gt;} serverMessages
 * @param {Array&lt;{id: string, createdAt?: string, timestamp?: string}&gt;} previous
 */
export function mergeServerAndLocalMessages(serverMessages, previous) {
  const byId = new Map();
  serverMessages.forEach((m) => byId.set(m.id, m));
  previous.forEach((m) => {
    if (!byId.has(m.id)) byId.set(m.id, m);
  });
  const merged = Array.from(byId.values());
  merged.sort((a, b) => {
    const ta = String(a.createdAt || a.timestamp || '');
    const tb = String(b.createdAt || b.timestamp || '');
    return ta.localeCompare(tb);
  });
  return merged;
}
